import React, { useContext, useState } from 'react';
import { PracticeTypesPerInstitution } from './calendarModels';
import { iterationKey, repeatIfDaily } from '../components/calendar/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PracticeTypeGroups from './practiceTypeGroups';
import alpColors from '../alpColors';
import { CalendarUhaContext } from './calendarUhaContext';
import { PraksiskalenderAktivitetDto } from '../api/generated/studiested-generated-api';
import { WeekYear } from '../date.utils';
import { styled } from '@mui/system';

const StyledAccountBalanceIcon = styled(AccountBalanceIcon)({
  fontSize: 20,
  verticalAlign: 'bottom',
  marginRight: '16px',
  color: alpColors.pewter,
});

interface InstitutionHeaderProps {
  institutionNames: string[];
  columnsCount: number;
  collapsible: boolean;
  collapsed: boolean;
  onCollapseToggle: (b: boolean) => void;
}

const InstitutionHeader: React.FC<
  React.PropsWithChildren<InstitutionHeaderProps>
> = ({
  institutionNames,
  columnsCount,
  collapsible,
  collapsed,
  onCollapseToggle,
}) => {
  const collapseFunction = collapsible
    ? () => {
        onCollapseToggle(!collapsed);
      }
    : () => {};

  return (
    <thead>
      <tr onClick={collapseFunction}>
        <th colSpan={columnsCount + 1} className="institution-slug">
          <StyledAccountBalanceIcon />
          {institutionNames.slice(1).map((it, i, all) => {
            return (
              <span key={i}>
                {it}
                {i < all.length - 1 ? ' - ' : ''}
              </span>
            );
          })}
          {collapsible ? (
            <div className="collapse-toggle">
              {collapsed ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </div>
          ) : (
            ''
          )}
        </th>
      </tr>
    </thead>
  );
};

interface CalendarPerInstitutionProps {
  orderNo: number;
  practiceTypes: PracticeTypesPerInstitution;
  practicePeriodsFiltered: {
    [klasseId: string]: PraksiskalenderAktivitetDto[];
  };
  onActivityStatusFilterChanged: Function;
  institutionCodesSlug: string;
  checkIfGroupCanBeSafelyDeleted: (klasseId: string) => boolean;
}

const CalendarPerInstitution: React.FC<
  React.PropsWithChildren<CalendarPerInstitutionProps>
> = ({
  orderNo,
  practiceTypes,
  practicePeriodsFiltered,
  onActivityStatusFilterChanged,
  institutionCodesSlug,
  checkIfGroupCanBeSafelyDeleted,
}) => {
  const { filteredMode, weekNumbers, isWeekly } =
    useContext(CalendarUhaContext);

  const expandedSessionKey = `expandedInCalendar-${institutionCodesSlug}`;

  const initCollapsed = () => {
    const keyFromStorage = sessionStorage.getItem(expandedSessionKey);

    if (keyFromStorage) {
      return keyFromStorage !== 'expanded';
    }

    return orderNo > 0;
  };

  const [collapsed, setCollapsed] = useState<boolean>(initCollapsed());

  const collapsible = !filteredMode;
  return (
    <div
      className={`calendar-width-limiter ${
        collapsible && collapsed ? 'collapsed' : ''
      }`}
    >
      <table className="calendar-per-institution">
        <InstitutionHeader
          institutionNames={practiceTypes.institutionNames}
          columnsCount={weekNumbers.length * (isWeekly ? 1 : 7)}
          collapsible={collapsible}
          collapsed={collapsed}
          onCollapseToggle={(shouldCollapse: boolean) => {
            sessionStorage.setItem(
              expandedSessionKey,
              shouldCollapse ? 'collapsed' : 'expanded',
            );
            setCollapsed(shouldCollapse);
          }}
        />
        <tbody>
          {practiceTypes.practiceTypes.map((it, i, all) => (
            <PracticeTypeGroups
              key={it.emneCode}
              practiceType={it}
              practicePeriodsFiltered={practicePeriodsFiltered}
              shouldRenderHeader={i === 0 || it.code !== all[i - 1].code}
              onActivityStatusFilterChanged={onActivityStatusFilterChanged}
              checkIfGroupCanBeSafelyDeleted={checkIfGroupCanBeSafelyDeleted}
            />
          ))}
          <tr className="divider">
            <td>&nbsp;</td>
            {weekNumbers.map((it: WeekYear) =>
              repeatIfDaily(isWeekly, (iDay) => (
                <td key={iterationKey(it, iDay)}>&nbsp;</td>
              )),
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CalendarPerInstitution;
